import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const { brandName } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || attempts >= 5) {
        if (attempts >= 5) {
          setStatus("timeout");
        }
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          // Update local storage token to refresh user data
          const token = localStorage.getItem(`vexl_token_${brandName}`);
          if (token) {
            // User will see updated subscription status on next load
          }
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else {
          // Keep polling
          setAttempts((a) => a + 1);
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (error) {
        setStatus("error");
      }
    };

    checkPaymentStatus();
  }, [sessionId, brandName, attempts]);

  return (
    <div className="min-h-screen bg-vexl-bg flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-[#CDF22B] mx-auto animate-spin mb-6" />
            <h1 className="font-display text-2xl font-bold text-white mb-2">
              Processing Payment...
            </h1>
            <p className="text-vexl-text-secondary">
              Please wait while we confirm your payment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#CDF22B]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#CDF22B]" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white mb-2">
              Payment Successful!
            </h1>
            <p className="text-vexl-text-secondary mb-8">
              Welcome to the program! You now have full access to all workouts and features.
            </p>
            <Button
              data-testid="go-to-app-btn"
              onClick={() => navigate(`/${brandName}`)}
              className="bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
            >
              Go to App
            </Button>
          </>
        )}

        {status === "expired" && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">
              Session Expired
            </h1>
            <p className="text-vexl-text-secondary mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Button
              onClick={() => navigate(`/${brandName}`)}
              className="bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
            >
              Back to App
            </Button>
          </>
        )}

        {status === "timeout" && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">
              Payment Pending
            </h1>
            <p className="text-vexl-text-secondary mb-8">
              We couldn't confirm your payment yet. If you completed the payment, it should reflect shortly.
            </p>
            <Button
              onClick={() => navigate(`/${brandName}`)}
              className="bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
            >
              Back to App
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-vexl-text-secondary mb-8">
              We couldn't verify your payment. Please contact support.
            </p>
            <Button
              onClick={() => navigate(`/${brandName}`)}
              className="bg-[#CDF22B] text-vexl-bg hover:bg-[#b8dc1f] rounded-full"
            >
              Back to App
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
